/**
 * Minimal, defensive CSS-selector + text derivation for semantic events
 * (story 2.3). Produces a short, reasonably stable selector for a clicked
 * element and a truncated text label. Never throws (the SDK must never break
 * the host page); degrades to the tag name or `''`.
 */

const MAX_TEXT = 100
const MAX_CLASSES = 3
const MAX_CLASS_LEN = 30
const MAX_DEPTH = 4

/**
 * Build a short CSS selector for `el`:
 *   - `tag#id` when the element has an id,
 *   - else `tag.class1.class2` (≤ 3 short classes),
 *   - else a depth-bounded `tag:nth-of-type(n)` ancestor path (≤ 4 levels).
 * Returns `''` for a non-element / null, or the bare tag name on any failure.
 */
export function cssSelector(el: Element | null): string {
  if (!el || el.nodeType !== 1) return ''
  try {
    // `localName` is lowercase for HTML but case-preserved for SVG
    // (`linearGradient`, `clipPath`) — `tagName.toLowerCase()` would corrupt those.
    const tag = el.localName
    if (el.id) return `${tag}#${el.id}`

    const classes = Array.from(el.classList)
      .filter((c) => c.length > 0 && c.length <= MAX_CLASS_LEN)
      .slice(0, MAX_CLASSES)
    if (classes.length > 0) return `${tag}.${classes.join('.')}`

    // Depth-bounded structural path.
    const parts: string[] = []
    let node: Element | null = el
    let depth = 0
    while (node && node.nodeType === 1 && depth < MAX_DEPTH) {
      const t = node.localName
      if (node.id) {
        parts.unshift(`${t}#${node.id}`)
        break
      }
      const parent: Element | null = node.parentElement
      if (parent) {
        const current = node
        const sameTag = Array.from(parent.children).filter((c) => c.tagName === current.tagName)
        if (sameTag.length > 1) {
          parts.unshift(`${t}:nth-of-type(${sameTag.indexOf(current) + 1})`)
        } else {
          parts.unshift(t)
        }
      } else {
        parts.unshift(t)
      }
      node = node.parentElement
      depth++
    }
    return parts.join(' > ')
  } catch {
    return el.localName ?? ''
  }
}

/** Trimmed, whitespace-collapsed, truncated `textContent` of `el` (≤ 100 chars). */
export function elementText(el: Element | null): string {
  if (!el) return ''
  try {
    const text = (el.textContent ?? '').trim().replace(/\s+/g, ' ')
    return text.length > MAX_TEXT ? text.slice(0, MAX_TEXT) : text
  } catch {
    return ''
  }
}
